"use client";

import type { ButtonHTMLAttributes, MouseEvent } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import {
  type ActiveProp,
  type BorderProp,
  type ChildrenProp,
  type ClassNameProp,
  type ElevatedProp,
  type TrackingProps,
  polymorphicForwardRef,
  type IconNodeProp,
} from "../../props";
import type { RoundedProp } from "../../props/RoundedProp";
import { Icon, type IconProps } from "../Icon";
import { useTouch } from "../../hooks/useTouch";
import { useTracker } from "../../hooks/useTracker";

import { focusVisibleInset } from "../../theme/css/focusVisible.css";
import { borders } from "../../theme/css/borders.css";
import { elevations } from "../../theme/css/elevations.css";
import {
  iconButtonStyle,
  inlineSizedIconStyle,
  iconStyle,
} from "./IconButton.css";
import { buttonVariantStyle } from "../Button/Button.css";
import { useParentHover } from "../../hooks";

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  ChildrenProp &
  ClassNameProp &
  BorderProp &
  RoundedProp &
  ElevatedProp &
  ActiveProp &
  RecipeVariants<typeof iconButtonStyle> &
  RecipeVariants<typeof buttonVariantStyle> &
  TrackingProps &
  IconNodeProp & {
    iconSize?: IconProps["size"];
    iconClassName?: string;
    iconAnimation?: IconProps["animation"];
  };

/**
 * A button with an icon.
 * @todo Tracking
 */
export const IconButton = polymorphicForwardRef<"button", IconButtonProps>(
  (
    {
      as: Element = "button",
      rounded,
      type = "button",
      variant = "primary",
      size,
      icon,
      border,
      iconSize: iconSizeProp,
      elevated = false,
      active = false,
      tracking,
      children,
      alignSelf,
      iconAnimation,
      className,
      iconClassName,
      onClick,
      ...props
    },
    forwardedRef,
  ) => {
    const isTouchDevice = useTouch();
    const { track } = useTracker();
    const { parentHover } = useParentHover();

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (tracking) {
        track("interaction", {
          category: "button",
          action: "click",
          label: tracking,
          properties: {
            buttonId: tracking,
            buttonType: variant,
            buttonSize: size,
            hasIcon: !!icon,
          },
        });
      }
    };

    const iconSize: IconProps["size"] = (() => {
      if (iconSizeProp) {
        return iconSizeProp;
      }

      switch (size) {
        case "xxs2":
          return "xs";
        case "xxs":
          return "xs";
        case "xs":
          return "s";
        case "s":
          return "s2";
        case "m":
          return "m";
        case "l":
          return "l";
        default:
          return "m";
      }
    })();

    return (
      <Element
        ref={forwardedRef}
        className={mergeClasses(
          iconButtonStyle({ size, rounded, alignSelf }),
          buttonVariantStyle({ variant }),
          border ? borders[border] : undefined,
          focusVisibleInset,
          isTouchDevice ? "--touch" : undefined,
          elevated ? elevations.l : undefined,
          className,
        )}
        type={type}
        onClick={handleClick}
        {...(active || parentHover ? { "data-active": "true" } : {})}
        {...props}
      >
        {icon && (
          <Icon
            className={mergeClasses(
              iconClassName,
              iconStyle,
              size === "inline" ? inlineSizedIconStyle : undefined,
            )}
            icon={icon}
            size={size === "inline" ? undefined : iconSize}
            color={variant === "gradient" ? "gradient" : undefined}
            animation={iconAnimation}
          />
        )}
        {children}
      </Element>
    );
  },
);

IconButton.displayName = "IconButton";
